import { API_URL } from "./config";
// src/SignPage.js
import React, { useEffect, useState, useRef } from "react";
import { useParams } from "react-router-dom";
import SignatureCanvas from "react-signature-canvas";
import { Document, Page } from "react-pdf";
import "react-pdf/dist/esm/Page/AnnotationLayer.css";
import "react-pdf/dist/esm/Page/TextLayer.css";

const SignPage = () => {
  const { token } = useParams();
  const [pdfUrl, setPdfUrl] = useState(null);
  const [numPages, setNumPages] = useState(null);
  const [myPlacements, setMyPlacements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [signatureDataUrl, setSignatureDataUrl] = useState(null);
  const [signedFile, setSignedFile] = useState(null);

  const sigCanvas = useRef(null);
  const user = JSON.parse(localStorage.getItem("user")) || { email: "" };

  useEffect(() => {
    const fetchData = async () => {
      try {
        // 1️⃣ Load PDF
        const res = await fetch(`${API_URL}/api/get-document/${token}`);
        if (!res.ok) throw new Error("Failed to load PDF");
        setPdfUrl(`${API_URL}/api/get-document/${token}`);

        // 2️⃣ Load all placements
        const posRes = await fetch(`${API_URL}/api/get-signers/${token}`);
        const posData = await posRes.json();

        if (posRes.ok && posData.placements) {
          // filter only my placements; _key is a UI-only id for React keys
          const mine = posData.placements
            .map((p, i) => ({ ...p, _key: i }))
            .filter((p) => p.email === user.email);
          setMyPlacements(mine);

          if (mine.length === 0) {
            throw new Error("No signature fields assigned to you");
          }
        } else {
          throw new Error("No placements found");
        }
      } catch (err) {
        console.error(err);
        setError("❌ Could not load PDF or placement.");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [token, user.email]);

  const handleClear = () => {
    sigCanvas.current.clear();
    setSignatureDataUrl(null);
  };

  const handleSaveSignature = () => {
    if (sigCanvas.current.isEmpty()) {
      alert("Please draw your signature first!");
      return;
    }
    setSignatureDataUrl(sigCanvas.current.getTrimmedCanvas().toDataURL("image/png"));
  };

  const handleSign = async () => {
    try {
      if (!signatureDataUrl || myPlacements.length === 0) {
        alert("⚠️ No signature or placement available.");
        return;
      }

      // send signature for each assigned placement
      const res = await fetch(`${API_URL}/api/sign/${token}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          signature: signatureDataUrl,
          // send all my fields (without the UI-only _key)
          placements: myPlacements.map(({ _key, ...placement }) => placement),
        }),
      });

      const data = await res.json();
      if (res.ok) {
        alert("✅ Document signed!");
        setSignedFile(data.signed_file);
      } else {
        alert("❌ Failed: " + data.error);
      }
    } catch (err) {
      console.error(err);
      alert("❌ Failed to sign document");
    }
  };

  if (loading) return <p>Loading document...</p>;
  if (error) return <p>{error}</p>;

  return (
    <div className="max-w-4xl mx-auto p-4">
      <h2 className="text-xl font-bold mb-4">Sign Document</h2>

      {/* PDF Viewer */}
      <Document
        file={pdfUrl}
        onLoadSuccess={({ numPages }) => setNumPages(numPages)}
      >
        {Array.from(new Array(numPages), (el, index) => (
          <div key={`page_${index + 1}`} className="relative my-4 border">
            <Page pageNumber={index + 1} width={600} />

            {/* Show only my assigned fields */}
            {myPlacements
              .filter((p) => p.page === index + 1)
              .map((p) => (
                <div
                  key={p._key}
                  className="absolute border-2 border-green-500 bg-yellow-100 text-xs flex items-center justify-center"
                  style={{
                    left: p.x,
                    top: p.y,
                    width: p.width,
                    height: p.height,
                  }}
                >
                  ✍️ Sign Here
                </div>
              ))}
          </div>
        ))}
      </Document>

      {/* Signature Pad */}
      <div className="mt-6">
        <p className="font-medium">Draw Your Signature:</p>
        <SignatureCanvas
          ref={sigCanvas}
          penColor="black"
          canvasProps={{ width: 500, height: 200, className: "border rounded" }}
        />
        <div className="mt-2 flex gap-2">
          <button
            onClick={handleClear}
            className="bg-gray-500 text-white px-3 py-1 rounded hover:bg-gray-600"
          >
            Clear
          </button>
          <button
            onClick={handleSaveSignature}
            className="bg-green-600 text-white px-3 py-1 rounded hover:bg-green-700"
          >
            Save Signature
          </button>
        </div>
      </div>

      <button
        onClick={handleSign}
        className="mt-6 bg-blue-600 text-white py-2 px-4 rounded hover:bg-blue-700"
      >
        Sign Document
      </button>

      {signedFile && (
        <div className="mt-4">
          <a
            href={`${API_URL}/api/download-signed/${token}`}
            target="_blank"
            rel="noreferrer"
            className="bg-green-600 text-white py-2 px-4 rounded hover:bg-green-700"
          >
            📥 Download Signed PDF
          </a>
        </div>
      )}
    </div>
  );
};

export default SignPage;